// workboxAtoms.ts
import { atom } from 'jotai';

// --- The state atom everyone consumes ---
export const workboxFoundAtom = atom(false);

// --- A write-only atom that kicks off polling ---
export const startWorkboxPollingAtom = atom(null, (get, set) => {
  let interval: ReturnType<typeof setInterval> | null = null;
  let delay = 500;

  async function checkWorkboxCache(): Promise<boolean> {
    if (!('caches' in window)) return false;

    const cacheKeys = await caches.keys();
    const cacheKey = cacheKeys.find((key) =>
      key.startsWith('workbox-precache'),
    );
    if (!cacheKey) return false;

    const cache = await caches.open(cacheKey);
    if (!cache) return false;

    const keys = await cache.keys();
    if (keys.length === 0) return false;

    // The app shell is precached as BASE_URL ("/") since #10138; service
    // workers from older builds still hold it under /index.html.
    const indexed = keys.find((k) => {
      const { pathname } = new URL(k.url);
      return (
        pathname === import.meta.env.BASE_URL ||
        pathname.endsWith('/index.html')
      );
    });
    return !!indexed;
  }

  const startPolling = () => {
    if (interval) clearInterval(interval);

    interval = setInterval(async () => {
      const found = await checkWorkboxCache();
      if (found) {
        set(workboxFoundAtom, true);
        // Switch to slower polling
        clearInterval(interval!);
        interval = setInterval(async () => {
          const stillThere = await checkWorkboxCache();
          set(workboxFoundAtom, stillThere);
        }, 10000);
      } else {
        set(workboxFoundAtom, false);
      }
    }, delay);
  };

  startPolling();

  // Return cleanup so React can stop it on unmount
  return () => {
    if (interval) clearInterval(interval);
  };
});
